import { useCallback, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  AlertCircle,
  ArrowRight,
  Award,
  Building2,
  CheckCircle2,
  CheckSquare,
  Circle,
  ClipboardList,
  FileText,
  Globe,
  Landmark,
  Lock,
  Loader2,
  Paperclip,
  Shield,
  TrendingUp,
  Users,
  Wand2,
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { useOnboardingForm, type FieldDiff } from "@/hooks/useOnboardingForm";
import { useIsMobile } from "@/hooks/use-mobile";
import { FileUploadSection } from "@/components/onboarding/shared/FileUploadSection";
import { CompanyIdentificationSection } from "@/components/onboarding/formular-notificare/CompanyIdentificationSection";
import { FinancialDataSection } from "@/components/onboarding/formular-notificare/FinancialDataSection";
import { ServiceSelectionSection } from "@/components/onboarding/formular-notificare/ServiceSelectionSection";
import { ClassificationSection } from "@/components/onboarding/formular-notificare/ClassificationSection";
import { Article9Section } from "@/components/onboarding/formular-notificare/Article9Section";
import EntityStateRelationSection from "@/components/onboarding/shared/EntityStateRelationSection";
import EUServicesSection from "@/components/onboarding/shared/EUServicesSection";
import { OnboardingFooter } from "@/components/onboarding/shared/OnboardingFooter";
import { ContactsSection } from "@/components/onboarding/formular-notificare/ContactsSection";
import { ConfirmationsSection } from "@/components/onboarding/formular-notificare/ConfirmationsSection";
import { DocumenteAnexateSection } from "@/components/onboarding/formular-notificare/DocumenteAnexateSection";
import { OnboardingConfirmModal } from "@/components/onboarding/formular-notificare/OnboardingConfirmModal";
import { cn } from "@/lib/utils";
import type { CompanyDataResult } from "@/services/company-data-service";

type SectionConfig = {
  id: string;
  label: string;
  icon: React.ElementType;
  subtitle: string;
  completionKey?: keyof ReturnType<
    typeof useOnboardingForm
  >["sectionCompletion"];
  isVisible?: (form: ReturnType<typeof useOnboardingForm>) => boolean;
  isArticle9?: boolean;
};

const SECTIONS: SectionConfig[] = [
  {
    id: "company",
    label: "Identificare Companie",
    icon: Building2,
    subtitle: "CUI, date companie, CAEN",
    completionKey: "company",
  },
  {
    id: "financial",
    label: "Date Financiare",
    icon: TrendingUp,
    subtitle: "Angajați, cifra afaceri, active",
    completionKey: "financial",
  },
  {
    id: "contacts",
    label: "Date Contact",
    icon: Users,
    subtitle: "Persoane de contact, date operaționale",
    completionKey: "contacts",
  },
  {
    id: "services",
    label: "Servicii NIS2",
    icon: Shield,
    subtitle: "Selectare servicii Anexa I/II",
    completionKey: "services",
  },
  {
    id: "relatie_stat",
    label: "Relația cu Statul Român",
    icon: Landmark,
    subtitle: "Stabilire, sediu, administrație publică, entitate critică",
    completionKey: "relatie_stat",
  },
  {
    id: "servicii_ue",
    label: "Servicii în UE",
    icon: Globe,
    subtitle: "Furnizare servicii în România și/sau UE",
    completionKey: "servicii_ue",
  },
  {
    id: "article9",
    label: "Art. 9 - Evaluare Impact",
    icon: FileText,
    subtitle: "Aplicarea Art. 9 conform Art. 10 alin. (2)",
    completionKey: "article9",
    isVisible: (form) => form.article9Enabled || form.needsArticle9,
    isArticle9: true,
  },
  {
    id: "classification",
    label: "Clasificare NIS2",
    icon: Award,
    subtitle: "Clasificare manuală",
    completionKey: "classification",
  },
  {
    id: "documente_anexate",
    label: "Documente Anexate",
    icon: Paperclip,
    subtitle: "Documente justificative",
    completionKey: "documente_anexate",
  },
  {
    id: "confirmations",
    label: "Confirmări",
    icon: CheckSquare,
    subtitle: "Confirmare exactitate și obligații",
    completionKey: "confirmations",
  },
];

function FormularNotificare() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const intentIsUpload = searchParams.get("intent") === "upload";

  const form = useOnboardingForm();
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const [fieldSelectionDialog, setFieldSelectionDialog] = useState<{
    open: boolean;
    variant: "company" | "financial";
    diffs: FieldDiff[];
    result: CompanyDataResult;
  } | null>(null);

  const handleFetchForSection = useCallback(
    async (variant: "company" | "financial") => {
      const cui = form.entityData.cui;
      if (!cui) return;

      try {
        const result = await form.fetchCompanyDataMutation.mutateAsync({ cui });
        if (!result.success) return;

        const diffs = form.getFieldDiffs(variant, result);
        const fields =
          variant === "company"
            ? [
                "cui",
                "nume_entitate",
                "cif",
                "adresa",
                "telefon",
                "caen_principal",
              ]
            : ["nr_salariati", "cifra_afaceri", "active"];

        if (diffs.length > 0) {
          setFieldSelectionDialog({ open: true, variant, diffs, result });
        } else {
          form.applyCompanyData(variant, fields, result);
        }
      } catch {
        // Error is handled by the mutation's error state
      }
    },
    [form]
  );

  const handleFieldSelectionConfirm = useCallback(
    (selectedFields: string[]) => {
      if (!fieldSelectionDialog) return;
      const { variant, result } = fieldSelectionDialog;

      const allFields =
        variant === "company"
          ? [
              "cui",
              "nume_entitate",
              "cif",
              "adresa",
              "telefon",
              "caen_principal",
            ]
          : ["nr_salariati", "cifra_afaceri", "active"];

      const nonConflictFields = allFields.filter(
        (f) => !fieldSelectionDialog.diffs.some((d) => d.field === f)
      );
      const fieldsToApply = [...nonConflictFields, ...selectedFields];

      form.applyCompanyData(variant, fieldsToApply, result);
      setFieldSelectionDialog(null);
    },
    [fieldSelectionDialog, form]
  );

  const handleSaveClick = useCallback(() => {
    if (form.isPaywallRequired) {
      form.openPaywall();
      return;
    }
    if (!form.validateForm()) return;
    setShowConfirmModal(true);
  }, [form]);

  const sectionErrorCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    const errorKeys = Object.keys(form.fieldErrors);
    if (errorKeys.length === 0) return counts;

    const exactMap: Record<string, string> = {
      cui: "company",
      nume_entitate: "company",
      adresa: "company",
      telefon: "company",
      email: "company",
      caen_principal: "company",
      nr_salariati: "financial",
      cifra_afaceri: "financial",
      active: "financial",
      selectedServices: "services",
      article9: "article9",
      manualClassification: "classification",
      confirmaExactitate: "confirmations",
      acceptaObligatii: "confirmations",
    };

    const prefixMap: Record<string, string> = {
      reprezentantLegal: "contacts",
      responsabilSecuritate: "contacts",
      contactOperational: "contacts",
      relatie_stat_roman: "relatie_stat",
      situatie_servicii_ue: "servicii_ue",
      documente_anexate: "documente_anexate",
    };

    for (const key of errorKeys) {
      let sectionId = exactMap[key];
      if (!sectionId) {
        for (const [prefix, section] of Object.entries(prefixMap)) {
          if (key.startsWith(prefix)) {
            sectionId = section;
            break;
          }
        }
      }
      if (sectionId) {
        counts[sectionId] = (counts[sectionId] ?? 0) + 1;
      }
    }

    return counts;
  }, [form.fieldErrors]);

  if (form.isLoadingEntity) {
    return <FormularNotificareSkeleton />;
  }

  const sectionParam = searchParams.get("section");
  const baseOpenSections = intentIsUpload
    ? ["company"]
    : ["company", "financial"];
  const defaultOpenSections =
    sectionParam && !baseOpenSections.includes(sectionParam)
      ? [...baseOpenSections, sectionParam]
      : baseOpenSections;

  return (
    <div className="container mx-auto px-4 md:py-8 py-4">
      <div className="max-w-4xl mx-auto space-y-3 md:space-y-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 md:w-12 md:h-12 bg-blue-100 rounded-xl flex items-center justify-center">
            <ClipboardList
              size={isMobile ? 20 : 24}
              className="text-blue-600"
            />
          </div>
          <div>
            <h1
              data-testid="onboarding-page-title"
              className="text-xl md:text-2xl font-heading font-bold text-slate-800"
            >
              Date Entitate
            </h1>
            <p className="text-xs md:text-sm text-slate-500">
              Completează informațiile necesare pentru identificarea NIS2
            </p>
          </div>
        </div>

        <div className="sticky top-0 z-10 -mx-4 px-4 md:mx-0 md:px-0 bg-background pt-2 pb-2">
          <StickyProgressBar completionPercentage={form.completionPercentage} />
        </div>

        <SectionBadgesCard sectionCompletion={form.sectionCompletion} />

        <FileUploadSection
          onUpload={form.handleFileUpload}
          isExtracting={form.isExtractingFile}
          autoFocus={intentIsUpload}
        />

        <div className="border border-blue-200 bg-blue-50/50 rounded-xl p-4 sm:p-5 flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2.5 sm:gap-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center shrink-0">
                <Wand2 className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
              </div>
              <h3 className="font-heading font-semibold text-slate-800 text-sm">
                Generează formularul automat
              </h3>
            </div>
            <p className="text-xs text-slate-500 mt-1.5">
              Introdu doar CUI-ul și obții formularul complet în mai puțin de 5
              minute. Datele se preiau automat din ANAF, serviciile sunt
              identificate cu AI, iar clasificarea NIS2 se face instant.
            </p>
          </div>
          <Button
            onClick={() => navigate("/identificare-raportare")}
            className="shrink-0 gap-2"
          >
            Completează automat
            <ArrowRight className="w-4 h-4" />
          </Button>
        </div>

        <Accordion
          type="multiple"
          defaultValue={defaultOpenSections}
          className="space-y-2 md:space-y-3"
        >
          {SECTIONS.filter(
            (section) => !section.isVisible || section.isVisible(form)
          ).map((section) => {
            const isComplete = section.completionKey
              ? form.sectionCompletion[section.completionKey]
              : false;
            const SectionIcon = section.icon;
            const errorCount = sectionErrorCounts[section.id] ?? 0;
            const hasErrors = errorCount > 0;

            return (
              <AccordionItem
                key={section.id}
                value={section.id}
                className={cn(
                  "border rounded-xl overflow-hidden shadow-sm data-[state=open]:shadow-md transition-all duration-300",
                  hasErrors
                    ? "border-red-200 bg-red-50/30"
                    : "border-slate-200 bg-white"
                )}
              >
                <AccordionTrigger className="px-4 md:px-6 py-4 hover:no-underline hover:bg-slate-50 transition-colors">
                  <div className="flex items-center gap-3 flex-1">
                    <div
                      className={cn(
                        "w-8 h-8 rounded-lg flex items-center justify-center shrink-0",
                        hasErrors
                          ? ""
                          : section.isArticle9
                            ? form.needsArticle9
                              ? "bg-amber-100"
                              : "bg-slate-100"
                            : isComplete
                              ? "bg-blue-100"
                              : "bg-slate-100"
                      )}
                    >
                      {hasErrors ? (
                        <AlertCircle className="w-4 h-4 text-red-500" />
                      ) : (
                        <SectionIcon
                          className={cn(
                            "w-4 h-4",
                            section.isArticle9
                              ? form.needsArticle9
                                ? "text-amber-600"
                                : "text-slate-400"
                              : isComplete
                                ? "text-blue-600"
                                : "text-slate-400"
                          )}
                        />
                      )}
                    </div>
                    <div className="text-left">
                      <span className="text-sm md:text-base font-semibold text-slate-800">
                        {section.label}
                      </span>
                      <span className="block text-xs text-slate-500 font-normal">
                        {section.subtitle}
                      </span>
                    </div>
                    <div className="ml-auto mr-2">
                      {section.isArticle9 ? (
                        form.needsArticle9 ? (
                          isComplete ? (
                            <div className="flex items-center gap-1.5 text-xs font-medium bg-blue-100 text-blue-700 px-2.5 py-1 rounded-full">
                              <CheckCircle2 className="w-3.5 h-3.5" />
                              Complet
                            </div>
                          ) : hasErrors ? (
                            <div className="flex items-center gap-1.5 text-xs font-medium bg-red-50 text-red-600 px-2.5 py-1 rounded-full">
                              <AlertCircle className="w-3.5 h-3.5" />
                              {errorCount === 1
                                ? "1 eroare"
                                : `${errorCount} erori`}
                            </div>
                          ) : (
                            <div className="flex items-center gap-1.5 text-xs font-medium bg-amber-100 text-amber-700 px-2.5 py-1 rounded-full">
                              Necesar
                            </div>
                          )
                        ) : (
                          <div className="flex items-center gap-1.5 text-xs font-medium bg-slate-100 text-slate-400 px-2.5 py-1 rounded-full">
                            Opțional
                          </div>
                        )
                      ) : isComplete ? (
                        <div className="flex items-center gap-1.5 text-xs font-medium bg-blue-100 text-blue-700 px-2.5 py-1 rounded-full">
                          <CheckCircle2 className="w-3.5 h-3.5" />
                          Complet
                        </div>
                      ) : hasErrors ? (
                        <div className="flex items-center gap-1.5 text-xs font-medium bg-red-50 text-red-600 px-2.5 py-1 rounded-full">
                          <AlertCircle className="w-3.5 h-3.5" />
                          {errorCount === 1
                            ? "1 eroare"
                            : `${errorCount} erori`}
                        </div>
                      ) : (
                        <div className="flex items-center gap-1.5 text-xs font-medium bg-slate-100 text-slate-500 px-2.5 py-1 rounded-full">
                          <Circle className="w-3.5 h-3.5" />
                          Incomplet
                        </div>
                      )}
                    </div>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-4 md:px-6 pb-6">
                  <SectionContent
                    sectionId={section.id}
                    form={form}
                    onFetchForSection={handleFetchForSection}
                    fieldSelectionDialog={fieldSelectionDialog}
                    onFieldSelectionDialogChange={(open) => {
                      if (!open) setFieldSelectionDialog(null);
                    }}
                    onFieldSelectionConfirm={handleFieldSelectionConfirm}
                  />
                </AccordionContent>
              </AccordionItem>
            );
          })}
        </Accordion>

        <OnboardingFooter
          leftContent={`${form.completionPercentage}% completat`}
        >
          <Button
            onClick={handleSaveClick}
            disabled={
              form.isSaving ||
              !!form.duplicateCuiMatch ||
              form.isCreditCheckLoading
            }
            size="lg"
            variant="hero"
            className="min-w-[200px]"
          >
            {form.isSaving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Se salvează...
              </>
            ) : form.isPaywallRequired ? (
              <>
                <Lock className="w-4 h-4" />
                Salvează și continuă
              </>
            ) : (
              <>
                Salvează și continuă
                <ArrowRight className="w-4 h-4" />
              </>
            )}
          </Button>
        </OnboardingFooter>

        <OnboardingConfirmModal
          open={showConfirmModal}
          onOpenChange={setShowConfirmModal}
          onConfirm={form.handleSave}
          isSaving={form.isSaving}
          entityName={form.entityData.nume_entitate ?? ""}
          cui={form.entityData.cui ?? ""}
          selectedServices={form.selectedServices}
          classificationResult={form.classificationResult}
          completionPercentage={form.completionPercentage}
          contactData={form.contactData}
          needsArticle9={form.needsArticle9}
          article9Results={form.article9Results}
        />
      </div>
    </div>
  );
}

const SECTION_LABELS: Record<string, string> = {
  company: "Companie",
  financial: "Financiar",
  contacts: "Contacte",
  services: "Servicii",
  relatie_stat: "Relație Stat",
  servicii_ue: "Servicii UE",
  article9: "Art. 9",
  classification: "Clasificare",
  documente_anexate: "Documente",
  confirmations: "Confirmări",
};

function StickyProgressBar({
  completionPercentage,
}: {
  completionPercentage: number;
}) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 px-4 py-2.5 shadow-sm">
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-sm font-medium text-slate-700">
          Progres completare
        </span>
        <span className="font-mono text-xs font-bold text-blue-600 tabular-nums">
          {completionPercentage}%
        </span>
      </div>
      <Progress
        value={completionPercentage}
        className="h-2 bg-slate-100"
        indicatorClassName={cn(
          "transition-all duration-500",
          completionPercentage === 100 ? "bg-emerald-500" : "bg-blue-500"
        )}
      />
    </div>
  );
}

function SectionBadgesCard({
  sectionCompletion,
}: {
  sectionCompletion: Record<string, boolean>;
}) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4 mt-3">
      <span className="block text-xs font-medium uppercase tracking-wide text-slate-500 mb-2.5">
        Secțiuni formular
      </span>
      <div className="flex items-center gap-2 flex-wrap">
        {Object.entries(sectionCompletion).map(([key, isComplete]) => (
          <div
            key={key}
            className={cn(
              "flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full",
              isComplete
                ? "bg-blue-100 text-blue-700"
                : "bg-slate-100 text-slate-500"
            )}
          >
            {isComplete ? (
              <CheckCircle2 className="w-3.5 h-3.5" />
            ) : (
              <Circle className="w-3.5 h-3.5" />
            )}
            {SECTION_LABELS[key] || key}
          </div>
        ))}
      </div>
    </div>
  );
}

function SectionContent({
  sectionId,
  form,
  onFetchForSection,
  fieldSelectionDialog,
  onFieldSelectionDialogChange,
  onFieldSelectionConfirm,
}: {
  sectionId: string;
  form: ReturnType<typeof useOnboardingForm>;
  onFetchForSection: (variant: "company" | "financial") => void;
  fieldSelectionDialog: {
    open: boolean;
    variant: "company" | "financial";
    diffs: FieldDiff[];
    result: CompanyDataResult;
  } | null;
  onFieldSelectionDialogChange: (open: boolean) => void;
  onFieldSelectionConfirm: (selectedFields: string[]) => void;
}) {
  switch (sectionId) {
    case "company":
      return (
        <CompanyIdentificationSection
          entityData={form.entityData}
          onFieldChange={form.updateEntityField}
          fieldErrors={form.fieldErrors}
          onFetchCompanyData={() => onFetchForSection("company")}
          isFetchingCompanyData={form.fetchCompanyDataMutation.isPending}
          fetchCompanyDataResult={form.fetchCompanyDataResult.company}
          autoFilledFields={form.autoFilledFields}
          fieldDiffs={
            fieldSelectionDialog?.variant === "company"
              ? fieldSelectionDialog.diffs
              : []
          }
          showFieldSelectionDialog={
            fieldSelectionDialog?.open === true &&
            fieldSelectionDialog.variant === "company"
          }
          onFieldSelectionDialogChange={onFieldSelectionDialogChange}
          onFieldSelectionConfirm={onFieldSelectionConfirm}
          duplicateCuiMatch={form.duplicateCuiMatch}
        />
      );
    case "financial":
      return (
        <FinancialDataSection
          entityData={form.entityData}
          autoFilledFields={form.autoFilledFields}
          sizeEvaluation={form.sizeEvaluation}
          onFieldChange={form.updateEntityField}
          fieldErrors={form.fieldErrors}
          cui={form.entityData.cui || ""}
          onFetchCompanyData={() => onFetchForSection("financial")}
          isFetchingCompanyData={form.fetchCompanyDataMutation.isPending}
          fetchCompanyDataResult={form.fetchCompanyDataResult.financial}
          fieldDiffs={
            fieldSelectionDialog?.variant === "financial"
              ? fieldSelectionDialog.diffs
              : []
          }
          showFieldSelectionDialog={
            fieldSelectionDialog?.open === true &&
            fieldSelectionDialog.variant === "financial"
          }
          onFieldSelectionDialogChange={onFieldSelectionDialogChange}
          onFieldSelectionConfirm={onFieldSelectionConfirm}
        />
      );
    case "services":
      return (
        <ServiceSelectionSection
          entityData={form.entityData}
          selectedServices={form.selectedServices}
          onServicesChange={form.handleServicesChange}
          fieldErrors={form.fieldErrors}
        />
      );
    case "relatie_stat":
      return (
        <EntityStateRelationSection
          entityData={form.entityData}
          onFieldChange={form.updateRelatieStatField}
          onReprezentantChange={form.updateReprezentantRomania}
          fieldErrors={form.fieldErrors}
        />
      );
    case "servicii_ue":
      return (
        <EUServicesSection
          entityData={form.entityData}
          onFieldChange={form.updateSituatieServiciiField}
          onStatesChange={form.updateSituatieServiciiUEStates}
          onAddressesChange={form.updateSituatieServiciiUEAddresses}
          fieldErrors={form.fieldErrors}
        />
      );
    case "article9":
      return (
        <Article9Section
          enabled={form.article9Enabled}
          onToggle={form.toggleArticle9}
          article9Results={form.article9Results}
          onResultChange={form.updateArticle9Result}
          required={form.needsArticle9}
          fieldErrors={form.fieldErrors}
        />
      );
    case "classification":
      return (
        <ClassificationSection
          classificationResult={form.classificationResult}
          manualClassification={form.manualClassification}
          onClassificationChange={form.updateManualClassification}
          fieldErrors={form.fieldErrors}
        />
      );
    case "contacts":
      return (
        <ContactsSection
          contactData={form.contactData}
          onContactFieldChange={form.updateContactField}
          onContactMetaChange={form.updateContactMeta}
          fieldErrors={form.fieldErrors}
        />
      );
    case "documente_anexate":
      return (
        <DocumenteAnexateSection
          data={form.documenteAnexate}
          onChange={form.updateDocumenteAnexate}
          fieldErrors={form.fieldErrors}
        />
      );
    case "confirmations":
      return (
        <ConfirmationsSection
          contactData={form.contactData}
          onContactMetaChange={form.updateContactMeta}
          fieldErrors={form.fieldErrors}
        />
      );
    default:
      return null;
  }
}

function FormularNotificareSkeleton() {
  return (
    <div className="container mx-auto px-4 md:py-8 py-4">
      <div className="max-w-4xl mx-auto space-y-3 md:space-y-6">
        <div className="flex items-center gap-3">
          <Skeleton className="w-12 h-12 rounded-xl" />
          <div>
            <Skeleton className="h-7 w-48 mb-2" />
            <Skeleton className="h-5 w-64" />
          </div>
        </div>
        <Skeleton className="h-24 rounded-xl" />
        {[1, 2, 3, 4, 5].map((i) => (
          <Skeleton key={i} className="h-16 rounded-xl" />
        ))}
      </div>
    </div>
  );
}

export default FormularNotificare;
